import { Button, Col, ProgressBar, Row } from 'react-bootstrap';
import { IconBook } from '../../../../../shared/Icons';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { DashboardContractList } from './DashboardContractList';
import { ContractInfo } from '../dto/IDashContracts';
import { IContract } from '../../../../../model/shared/IContract';
import { DashboardContractListItem } from './DashboardContractListItem';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { hasAccessRights } from '../../../../../shared/functions/Functions';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import InfiniteScroll from 'react-infinite-scroll-component';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { DashboardContractCardSkeleton, DashboardContractRowsSkeleton } from './LongTermDashboardSkeletons';

interface IDashboardContractCardProps {
    contracts: IContract[];
    contractsInfo?: ContractInfo;
    filterParams: { page: number; perPage: number };
    setFilterParams: Dispatch<SetStateAction<{ page: number; perPage: number }>>;
    contractCount: number;
    isLoading?: boolean;
}

export function DashboardContractCard({
    contractsInfo,
    contracts,
    setFilterParams,
    filterParams,
    contractCount,
    isLoading = false,
}: IDashboardContractCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();
    const canViewContract = hasAccessRights(AccessRightEnum.CAN_VIEW_CONTRACTS);
    const hideCosts = hasAccessRights(AccessRightEnum.MEASURE_HIDE_COSTS);

    const linkToContract = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_CONTRACTS}/${id}`);
    };

    const handlePerPage = (numberPerPage: number) => {
        setFilterParams(prev => ({ ...prev, perPage: numberPerPage }));
    };

    if (isLoading) {
        return <DashboardContractCardSkeleton hideCosts={hideCosts} />;
    }

    return (
        <CardLayout classCard="mb-3">
            <div className="d-flex justify-content-between">
                <div
                    className={`d-flex align-items-center textHover ${canViewContract ? 'cursor-pointer' : ''}`}
                    onClick={canViewContract ? linkToContract : undefined}
                >
                    <IconBook width="20" height="20" color={MUTED_TEXT_COLOR} classes={' mr-2'} />
                    <h6 className="mb-0 ">{t('brand.campaign.dashboard.contracts.contracts')}</h6>
                </div>
                {canViewContract && (
                    <Button size="sm" onClick={linkToContract}>
                        {t('general.button.open')}
                    </Button>
                )}
            </div>
            <Row className="align-items-center my-3 justify-content-between">
                <Col md={6} className="d-flex justify-content-between justify-content-lg-start">
                    <div>
                        <h6 className="text-primary">{contractsInfo?.bookedPosts ?? '0'}</h6>
                        <p>{t('brand.campaign.dashboard.contracts.postings')}</p>
                    </div>
                    {!hideCosts && (
                        <div className="mx-md-2 mx-lg-4">
                            <h6 className="text-primary euro-symbol">{priceSplitter(contractsInfo?.cpm as number)}</h6>
                            <p>{t('brand.campaign.dashboard.contracts.avg_cpm')}</p>
                        </div>
                    )}
                    <div className="mx-md-2">
                        <h6 className="text-primary">{numberFormatter(contractsInfo?.reach as number)}</h6>
                        <p>{t('brand.campaign.dashboard.contracts.avg_reach')}</p>
                    </div>
                    <div>
                        <h6 className="text-primary">{contractsInfo?.influencersBooked ?? '0'}</h6>
                        <p>{t('brand.campaign.dashboard.booking.influencers_booked')}</p>
                    </div>
                </Col>
                {!hideCosts && (
                    <Col md={4} className="justify-content-end">
                        <div className="d-flex justify-content-between">
                            <div> {t('brand.campaign.dashboard.booking.budget_usage')}</div>
                            <div>
                                <span className="text-primary">
                                    {priceSplitter(contractsInfo?.totalSpent as number)}
                                </span>{' '}
                                / {priceSplitter(contractsInfo?.grossBudget)}
                            </div>
                            <div className="word-break-word">{contractsInfo?.budgetPercentage}%</div>
                        </div>
                        <ProgressBar
                            variant="primary"
                            className="progress-sm w-100 bg-primary-lighter"
                            now={contractsInfo?.budgetPercentage}
                        />
                    </Col>
                )}
                <Col xs={12}>
                    <div id="scrollableChildDiv" style={{ maxHeight: 290, overflow: 'auto' }}>
                        {contracts?.length > 0 ? (
                            <InfiniteScroll
                                dataLength={contracts?.length}
                                next={() => handlePerPage(filterParams?.perPage + 5)}
                                endMessage={
                                    <p
                                        className={`text-center ${
                                            contracts?.length !== contractCount || filterParams?.perPage <= 10
                                                ? 'd-none'
                                                : ''
                                        }`}
                                    >
                                        <b>You have seen all!</b>
                                    </p>
                                }
                                hasMore={contracts?.length < contractCount}
                                loader={
                                    <DashboardContractRowsSkeleton rows={2} withHeader={false} hideCosts={hideCosts} />
                                }
                                scrollableTarget="scrollableChildDiv"
                            >
                                <DashboardContractList>
                                    <>
                                        {contracts.map((contract, i) => (
                                            <DashboardContractListItem contract={contract} key={i} />
                                        ))}
                                    </>
                                </DashboardContractList>
                            </InfiniteScroll>
                        ) : (
                            <EmptyState
                                iconPath="/icons/img_external_search.svg"
                                desc={t('brand.campaign.dashboard.contracts.empty_state_desc')}
                                imgClass="w-80px"
                            />
                        )}
                    </div>
                </Col>
            </Row>
        </CardLayout>
    );
}
